import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  DatePeriod,
  Hasher,
  Kennel,
  MembershipScheme,
  RoleAssignment,
  UpdateMembershipSchemeRequest,
} from "@hash/shared";
import { api, type SchemeWithPeriods } from "../api/client";
import { loadHashers, searchHashers, useHashers } from "../stores/hasherStore";

const PAGE_SIZE = 100;

export function KennelsPage({ isAdmin = false }: { isAdmin?: boolean }) {
  const [query, setQuery] = useState("");
  const [kennels, setKennels] = useState<Kennel[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Kennel being edited: metadata for any kennel, plus fees + dues (membership
  // schemes) for managed ones (Stories 9.1, 10.3/10.4).
  const [editTarget, setEditTarget] = useState<Kennel | null>(null);
  // Kennel whose schedule we're setting (Story 3.4).
  const [scheduleTarget, setScheduleTarget] = useState<Kennel | null>(null);
  // Kennel whose misman roster we're managing (Story 11.3).
  const [mismanTarget, setMismanTarget] = useState<Kennel | null>(null);
  // Whether the create-kennel modal is open (Stories 3.1/3.2).
  const [creating, setCreating] = useState(false);

  // Hasher counts per kennel are tallied client-side from the shared roster
  // store (already loaded for search elsewhere) by homeKennelId — no per-kennel
  // server query. The roster is small and the store dedupes the fetch.
  const { hashers } = useHashers();
  const hasherCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const h of hashers) {
      counts[h.homeKennelId] = (counts[h.homeKennelId] ?? 0) + 1;
    }
    return counts;
  }, [hashers]);

  useEffect(() => {
    loadHashers().catch(() => {});
  }, []);

  const load = useCallback(
    async (q: string, nextCursor: string | undefined) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.kennels.list({
          q: q || undefined,
          cursor: nextCursor,
          limit: PAGE_SIZE,
        });
        setKennels((prev) =>
          nextCursor ? [...prev, ...res.kennels] : res.kennels
        );
        setCursor(res.cursor);
        setHasMore(Boolean(res.cursor));
      } catch (e) {
        console.error("Failed to load kennels", e);
        setError(e instanceof Error ? e.message : "Failed to load kennels");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const t = setTimeout(() => load(query.trim(), undefined), 200);
    return () => clearTimeout(t);
  }, [query, load]);

  const managedKennels = kennels.filter((k) => k.managed);
  const liteKennels = kennels.filter((k) => !k.managed);

  const renderRow = (k: Kennel) => {
    const count = hasherCounts[k.id] ?? 0;
    return (
      <li key={k.id} className="px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 truncate">{k.name}</div>
          <div className="text-sm text-slate-500">
            {count === 1 ? "1 hasher" : `${count} hashers`}
            {k.managed && ` · Door ${money(k.doorFee)}`}
          </div>
        </div>
        <button
          onClick={() => setEditTarget(k)}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
        >
          Edit
        </button>
        {k.managed ? (
          <>
            <button
              onClick={() => setScheduleTarget(k)}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Schedule
            </button>
            <button
              onClick={() => setMismanTarget(k)}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Misman
            </button>
          </>
        ) : (
          // Promotion to managed is admin-only (server-enforced); only
          // show the control to admins.
          isAdmin && (
            <button
              onClick={async () => {
                setError(null);
                try {
                  const updated = await api.kennels.promote(k.id);
                  setKennels((prev) =>
                    prev.map((x) => (x.id === updated.id ? updated : x))
                  );
                } catch (e) {
                  setError(extractError(e, "Failed to promote kennel"));
                }
              }}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Promote
            </button>
          )
        )}
        <button
          onClick={async () => {
            if (!confirm(`Delete kennel "${k.name}"?`)) return;
            setDeletingId(k.id);
            setError(null);
            try {
              await api.kennels.delete(k.id);
              setKennels((prev) => prev.filter((x) => x.id !== k.id));
            } catch (e) {
              setError(extractError(e, "Failed to delete kennel"));
            } finally {
              setDeletingId(null);
            }
          }}
          disabled={deletingId === k.id}
          aria-label="Delete"
          className="w-11 h-11 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 active:bg-red-50 disabled:opacity-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      </li>
    );
  };

  return (
    <div className="min-h-screen pb-24">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-slate-900">Kennels</h2>
          <button
            onClick={() => setCreating(true)}
            className="shrink-0 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white active:bg-slate-700"
          >
            + New kennel
          </button>
        </div>
        <input
          type="search"
          inputMode="search"
          placeholder="Search kennels…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mt-2 w-full h-12 px-4 rounded-lg bg-slate-100 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
        />
      </header>

      {error && (
        <p className="px-4 py-3 text-sm text-red-600">{error}</p>
      )}

      {kennels.length === 0 && !loading ? (
        <p className="px-4 py-6 text-sm text-slate-500">No kennels found.</p>
      ) : (
        <>
          <KennelSection
            title="Managed kennels"
            kennels={managedKennels}
            renderRow={renderRow}
          />
          <KennelSection
            title="Lite kennels"
            kennels={liteKennels}
            renderRow={renderRow}
          />
        </>
      )}

      <div className="px-4 py-4">
        {hasMore ? (
          <button
            onClick={() => load(query.trim(), cursor)}
            disabled={loading}
            className="w-full h-12 rounded-lg bg-slate-900 text-white font-medium active:bg-slate-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        ) : loading ? (
          <p className="text-center text-sm text-slate-500">Loading…</p>
        ) : null}
      </div>

      {editTarget && (
        <EditKennelModal
          kennel={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setKennels((prev) =>
              prev.map((x) => (x.id === updated.id ? updated : x))
            );
            setEditTarget(null);
          }}
        />
      )}

      {scheduleTarget && (
        <ScheduleModal
          kennel={scheduleTarget}
          onClose={() => setScheduleTarget(null)}
        />
      )}

      {mismanTarget && (
        <KennelMismanModal
          kennel={mismanTarget}
          onClose={() => setMismanTarget(null)}
        />
      )}

      {creating && (
        <CreateKennelModal
          isAdmin={isAdmin}
          onClose={() => setCreating(false)}
          onCreated={(k) => {
            setKennels((prev) => [k, ...prev]);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * A titled group of kennel rows. Rendered only when the group is non-empty so
 * an empty Managed/Lite section doesn't leave a stray header. Rows come from the
 * parent's renderRow so the section stays presentational.
 */
function KennelSection({
  title,
  kennels,
  renderRow,
}: {
  title: string;
  kennels: Kennel[];
  renderRow: (k: Kennel) => React.ReactNode;
}) {
  if (kennels.length === 0) return null;
  return (
    <section>
      <h3 className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h3>
      <ul className="divide-y divide-slate-200 bg-white">
        {kennels.map(renderRow)}
      </ul>
    </section>
  );
}

/**
 * Create a kennel (Stories 3.1/3.2). Anyone may create a *reference* kennel
 * (to attribute visitors); the **Managed** toggle is admin-only and the server
 * enforces it (a managed kennel can have a schedule, trails, fees, and misman).
 * A schedule can be set afterward from the kennel's Schedule action (Story 3.4).
 */
function CreateKennelModal({
  isAdmin,
  onClose,
  onCreated,
}: {
  isAdmin: boolean;
  onClose: () => void;
  onCreated: (kennel: Kennel) => void;
}) {
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [founded, setFounded] = useState("");
  const [managed, setManaged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) {
      setErr("A long name is required.");
      return;
    }
    if (!shortName.trim()) {
      setErr("A short name is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.kennels.create({
        name: name.trim(),
        shortName: shortName.trim(),
        city: city.trim() || undefined,
        region: region.trim() || undefined,
        founded: founded.trim() || undefined,
        managed: isAdmin ? managed : undefined,
      });
      onCreated(created);
    } catch (e) {
      setErr(extractError(e, "Failed to create kennel"));
      setBusy(false);
    }
  };

  return (
    <Modal title="New kennel" onClose={onClose}>
      <p className="mt-1 text-sm text-slate-500">
        A reference kennel just attributes visiting hashers.
        {isAdmin && " Toggle Managed to give it trails, fees, and misman."}
      </p>
      <div className="mt-4 space-y-3">
        <label className="block">
          <span className={labelCls}>Long name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hash House Harriers of Somewhere"
            aria-label="Long name"
            autoFocus
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Short name</span>
          <input
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            placeholder="SomewhereH3"
            aria-label="Short name"
            className={inputCls}
          />
        </label>
        <div className="flex gap-2">
          <label className="block flex-1">
            <span className={labelCls}>City</span>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              aria-label="City"
              className={inputCls}
            />
          </label>
          <label className="block w-24">
            <span className={labelCls}>Region</span>
            <input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              aria-label="Region"
              className={inputCls}
            />
          </label>
        </div>
        <label className="block">
          <span className={labelCls}>Founded</span>
          <input
            type="date"
            value={founded}
            onChange={(e) => setFounded(e.target.value)}
            aria-label="Founded"
            className={inputCls}
          />
        </label>
        {isAdmin && (
          <button
            type="button"
            role="switch"
            aria-checked={managed}
            aria-label="Managed kennel"
            onClick={() => setManaged((m) => !m)}
            className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3"
          >
            <span className="font-medium text-slate-900">Managed kennel</span>
            <span
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                managed ? "bg-emerald-600" : "bg-slate-300"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  managed ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </span>
          </button>
        )}
      </div>
      {err && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {err}
        </p>
      )}
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create"}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Misman roster for a kennel (Story 11.3): list current grants,
 * appoint a claimed hasher, revoke with confirm. Grants/revokes take effect on
 * the appointee's next token refresh or login (~1h staleness accepted).
 */
function KennelMismanModal({
  kennel,
  onClose,
}: {
  kennel: Kennel;
  onClose: () => void;
}) {
  const [misman, setMisman] = useState<RoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  // Search the shared client-side store instead of the server.
  const { hashers: roster } = useHashers();

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.kennels.listMisman(kennel.id);
      setMisman(res.misman);
    } catch (e) {
      setErr(extractError(e, "Failed to load misman"));
    } finally {
      setLoading(false);
    }
  }, [kennel.id]);

  useEffect(() => {
    void reload();
    // Ensure the roster store is loaded (and refreshed if stale) for search.
    loadHashers().catch(() => {});
  }, [reload]);

  // Claimed-hasher search for the appoint flow. Only claimed accounts can be
  // misman — they have to be able to log in. Runs against the local store.
  const results = useMemo(
    () => searchHashers(query).filter((h) => h.accountState === "claimed"),
    [query, roster]
  );

  const grant = async (hasher: Hasher) => {
    setBusy(true);
    setErr(null);
    try {
      await api.kennels.grantMisman(kennel.id, hasher.id);
      setQuery("");
      await reload();
    } catch (e) {
      setErr(extractError(e, "Failed to appoint misman"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (r: RoleAssignment) => {
    if (!confirm(`Remove ${r.hasherName} as misman of ${kennel.name}?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api.kennels.revokeMisman(kennel.id, r.hasherId);
      await reload();
    } catch (e) {
      setErr(extractError(e, "Failed to remove misman"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`${kennel.name} misman`} onClose={onClose}>
      <p className="mt-1 text-sm text-slate-500">
        Misman manage this kennel's trails, fees, dues, and schedule. Changes
        take effect when the hasher next logs in or their session refreshes.
      </p>

      {err && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {err}
        </p>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">Loading…</p>
      ) : misman.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">No misman yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-200">
          {misman.map((r) => (
            <li key={r.hasherId} className="py-2.5 flex items-center gap-3">
              <span className="flex-1 min-w-0 font-medium text-slate-900 truncate">
                {r.hasherName}
              </span>
              <button
                type="button"
                onClick={() => revoke(r)}
                disabled={busy}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <label className="block mt-4">
        <span className={labelCls}>Appoint a misman</span>
        <input
          type="search"
          inputMode="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => loadHashers().catch(() => {})}
          placeholder="Search claimed hashers…"
          aria-label="Appoint a misman"
          className={inputCls}
        />
      </label>
      {query.trim() && (
        <ul className="mt-2 divide-y divide-slate-200">
          {results.length === 0 ? (
            <li className="py-2 text-sm text-slate-500">
              No claimed hashers match. (A misman must have logged in at least
              once.)
            </li>
          ) : (
            results.map((h) => (
              <li key={h.id} className="py-2 flex items-center gap-3">
                <span className="flex-1 min-w-0 truncate">
                  <span className="font-medium text-slate-900">{h.name}</span>
                  {h.homeKennel && (
                    <span className="ml-2 text-sm text-slate-500">
                      {h.homeKennel}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => grant(h)}
                  disabled={busy}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Appoint
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      <div className="mt-4">
        <button
          type="button"
          onClick={onClose}
          className="h-12 w-full rounded-lg bg-slate-100 font-medium text-slate-700"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Monthly "Nth weekday" options; -1 is the last occurrence in the month.
const NTH_OPTIONS = [
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: -1, label: "last" },
];

function ScheduleModal({
  kennel,
  onClose,
}: {
  kennel: Kennel;
  onClose: () => void;
}) {
  const existing = kennel.schedule;
  const [freq, setFreq] = useState<
    "weekly" | "biweekly" | "monthly" | "lunar"
  >(existing?.recurrence.freq ?? "weekly");
  const [weekday, setWeekday] = useState(existing?.recurrence.weekday ?? 3);
  const [nth, setNth] = useState(
    existing?.recurrence.freq === "monthly" ? existing.recurrence.nth : 1
  );
  const [phase, setPhase] = useState<"full" | "new">(
    existing?.recurrence.freq === "lunar" ? existing.recurrence.phase : "full"
  );
  const [time, setTime] = useState(existing?.time ?? "19:00");
  const [timeZone, setTimeZone] = useState(
    existing?.timeZone ?? "America/New_York"
  );
  const [horizonMonths, setHorizonMonths] = useState(
    existing?.horizonMonths ?? 6
  );
  const [extendBy, setExtendBy] = useState(3);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const recurrence =
    freq === "monthly"
      ? ({ freq: "monthly", nth, weekday } as const)
      : freq === "lunar"
        ? ({ freq: "lunar", phase, weekday } as const)
        : ({ freq, weekday } as const);

  const save = async () => {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const { generated } = await api.kennels.setSchedule(kennel.id, {
        recurrence,
        time,
        timeZone,
        horizonMonths,
      });
      setResult(`Generated ${generated} trail${generated === 1 ? "" : "s"}.`);
    } catch (e) {
      setErr(extractError(e, "Failed to save schedule"));
    } finally {
      setBusy(false);
    }
  };

  const extend = async () => {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const { generated } = await api.kennels.extendHorizon(kennel.id, extendBy);
      setResult(
        `Extended: generated ${generated} more trail${
          generated === 1 ? "" : "s"
        }.`
      );
    } catch (e) {
      setErr(extractError(e, "Failed to extend horizon"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-slate-900/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Schedule for ${kennel.name}`}
        className="w-full rounded-t-2xl bg-white p-5 sm:max-w-sm sm:rounded-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-slate-900">
          {kennel.name} schedule
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Generates placeholder trails out to the horizon. Saving never rewrites
          an existing trail.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className={labelCls}>Frequency</span>
            <select
              value={freq}
              onChange={(e) =>
                setFreq(
                  e.target.value as "weekly" | "biweekly" | "monthly" | "lunar"
                )
              }
              aria-label="Frequency"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base"
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="monthly">Monthly</option>
              <option value="lunar">Monthly (lunar phase)</option>
            </select>
          </label>

          {freq === "monthly" && (
            <label className="block">
              <span className={labelCls}>Which week</span>
              <select
                value={nth}
                onChange={(e) => setNth(Number(e.target.value))}
                aria-label="Which week"
                className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base"
              >
                {NTH_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {freq === "lunar" && (
            <label className="block">
              <span className={labelCls}>Lunar phase</span>
              <select
                value={phase}
                onChange={(e) => setPhase(e.target.value as "full" | "new")}
                aria-label="Lunar phase"
                className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base"
              >
                <option value="full">Full moon</option>
                <option value="new">New moon</option>
              </select>
            </label>
          )}

          <label className="block">
            <span className={labelCls}>
              {freq === "weekly" || freq === "biweekly" ? "Day" : "Weekday"}
            </span>
            <select
              value={weekday}
              onChange={(e) => setWeekday(Number(e.target.value))}
              aria-label="Day"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base"
            >
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Time</span>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              aria-label="Time"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base"
            />
          </label>
          <label className="block">
            <span className={labelCls}>Time zone</span>
            <input
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
              aria-label="Time zone"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base"
            />
          </label>
          <label className="block">
            <span className={labelCls}>Horizon (months)</span>
            <input
              type="number"
              min={1}
              max={24}
              value={horizonMonths}
              onChange={(e) => setHorizonMonths(Number(e.target.value))}
              aria-label="Horizon months"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base"
            />
          </label>
        </div>

        {result && <p className="mt-3 text-sm text-emerald-700">{result}</p>}
        {err && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {err}
          </p>
        )}
        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
          >
            Done
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
          >
            {busy ? "Generating…" : "Save & generate"}
          </button>
        </div>

        {existing && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <span className={labelCls}>Extend the horizon</span>
            <p className="mb-2 text-sm text-slate-500">
              Generate further trails past the last one, using the current
              schedule. (Save first if you changed the recurrence above.)
            </p>
            <div className="flex items-end gap-2">
              <label className="block flex-1">
                <span className={labelCls}>Add months</span>
                <input
                  type="number"
                  min={1}
                  max={24}
                  value={extendBy}
                  onChange={(e) => setExtendBy(Number(e.target.value))}
                  aria-label="Extend months"
                  className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base"
                />
              </label>
              <button
                onClick={extend}
                disabled={busy}
                className="h-12 rounded-lg border border-slate-300 px-4 font-medium text-slate-700 disabled:opacity-50"
              >
                Extend
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Unified kennel editor (Stories 9.1, 10.3/10.4). Edits metadata for any
 * kennel; for managed kennels it also surfaces the door fee and the membership
 * schemes (dues) in the same place. The long name is shown read-only — renaming
 * needs a denormalized-cache fan-out and is deferred (see UpdateKennelRequest).
 * Metadata + fee save together via PATCH; dues edits route through the reused
 * SchemeFormModal, which swaps in over this modal.
 */
function EditKennelModal({
  kennel,
  onClose,
  onSaved,
}: {
  kennel: Kennel;
  onClose: () => void;
  onSaved: (k: Kennel) => void;
}) {
  const [name, setName] = useState(kennel.name);
  const [shortName, setShortName] = useState(kennel.shortName ?? "");
  const [city, setCity] = useState(kennel.city ?? "");
  const [region, setRegion] = useState(kennel.region ?? "");
  const [founded, setFounded] = useState(kennel.founded ?? "");
  const [door, setDoor] = useState(dollars(kennel.doorFee));
  const [virginsFree, setVirginsFree] = useState(kennel.virginsFreeDoor ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Dues (managed only): membership schemes covering this kennel. Loaded
  // lazily; editing/creating swaps in the SchemeFormModal over this one.
  const [schemes, setSchemes] = useState<SchemeWithPeriods[]>([]);
  const [schemesLoading, setSchemesLoading] = useState(kennel.managed);
  const [editingScheme, setEditingScheme] = useState<
    MembershipScheme | "new" | null
  >(null);

  const reloadSchemes = useCallback(async () => {
    if (!kennel.managed) return;
    setSchemesLoading(true);
    try {
      const res = await api.schemes.list(kennel.id);
      setSchemes(res.schemes);
    } catch (e) {
      setErr(extractError(e, "Failed to load membership schemes"));
    } finally {
      setSchemesLoading(false);
    }
  }, [kennel.id, kennel.managed]);

  useEffect(() => {
    void reloadSchemes();
  }, [reloadSchemes]);

  const save = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErr("A long name is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const updated = await api.kennels.update(kennel.id, {
        // Only send the name when it changed — the server fans a rename out to
        // the cached homeKennel/kennel fields, so we don't trigger that for a
        // fee/metadata-only edit.
        ...(trimmedName !== kennel.name ? { name: trimmedName } : {}),
        shortName: shortName.trim() || undefined,
        city: city.trim() || undefined,
        region: region.trim() || undefined,
        founded: founded.trim() || undefined,
        // Door fee + virgins-free are managed-kennel concepts only.
        ...(kennel.managed
          ? { doorFee: cents(door), virginsFreeDoor: virginsFree }
          : {}),
      });
      onSaved(updated);
    } catch (e) {
      setErr(extractError(e, "Failed to save kennel"));
      setBusy(false);
    }
  };

  // Editing a dues scheme replaces this modal with the scheme form, mirroring
  // the old standalone dues modal's flow.
  if (editingScheme) {
    return (
      <SchemeFormModal
        kennel={kennel}
        scheme={editingScheme === "new" ? null : editingScheme}
        onClose={() => setEditingScheme(null)}
        onSaved={() => {
          setEditingScheme(null);
          void reloadSchemes();
        }}
      />
    );
  }

  return (
    <Modal title={`Edit ${kennel.name}`} onClose={onClose}>
      <div className="mt-3 space-y-3">
        <label className="block">
          <span className={labelCls}>Long name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hash House Harriers of Somewhere"
            aria-label="Long name"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Short name</span>
          <input
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            placeholder="SomewhereH3"
            aria-label="Short name"
            className={inputCls}
          />
        </label>
        <div className="flex gap-2">
          <label className="block flex-1">
            <span className={labelCls}>City</span>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              aria-label="City"
              className={inputCls}
            />
          </label>
          <label className="block w-24">
            <span className={labelCls}>Region</span>
            <input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              aria-label="Region"
              className={inputCls}
            />
          </label>
        </div>
        <label className="block">
          <span className={labelCls}>Founded</span>
          <input
            type="date"
            value={founded}
            onChange={(e) => setFounded(e.target.value)}
            aria-label="Founded"
            className={inputCls}
          />
        </label>

        {kennel.managed && (
          <>
            <div className="border-t border-slate-200 pt-3">
              <MoneyField
                label="Hash Cash (non-member)"
                value={door}
                onChange={setDoor}
              />
              <p className="mt-1 text-xs text-slate-500">
                Members are free at the door.
              </p>
              <button
                type="button"
                role="switch"
                aria-checked={virginsFree}
                aria-label="Virgins free"
                onClick={() => setVirginsFree((v) => !v)}
                className="mt-3 flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3 text-left"
              >
                <span>
                  <span className="block font-medium text-slate-900">
                    Virgins free
                  </span>
                  <span className="block text-xs text-slate-500">
                    First-timers skip the door fee.
                  </span>
                </span>
                <span
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full ${
                    virginsFree ? "bg-emerald-600" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      virginsFree ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </button>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between">
                <span className={labelCls}>Dues</span>
                <button
                  type="button"
                  onClick={() => setEditingScheme("new")}
                  className="text-sm font-medium text-slate-700 underline"
                >
                  + New scheme
                </button>
              </div>
              <p className="mb-1 text-xs text-slate-500">
                Membership is sold per period under a scheme. Changes only
                affect periods that haven't opened for purchase yet.
              </p>
              {schemesLoading ? (
                <p className="text-sm text-slate-500">Loading…</p>
              ) : schemes.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No membership scheme covers this kennel yet.
                </p>
              ) : (
                <ul className="divide-y divide-slate-200">
                  {schemes.map((s) => (
                    <li key={s.id} className="py-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">
                          {s.name}
                          {s.retired && (
                            <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-600">
                              retired
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-500">
                          {money(s.price)} per period · {describeSchedule(s)} ·
                          buy {s.windowDays} days early
                        </div>
                        {s.purchasable.length > 0 && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            Open now:{" "}
                            {s.purchasable
                              .map((p) => `${p.start} – ${p.end}`)
                              .join(", ")}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingScheme(s)}
                        className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                      >
                        Edit
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      {err && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {err}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

function describeSchedule(s: MembershipScheme): string {
  if (s.recurrence) {
    return `every ${s.recurrence.months} month${
      s.recurrence.months === 1 ? "" : "s"
    } from ${s.recurrence.anchor}`;
  }
  const n = s.periods?.length ?? 0;
  return `${n} fixed period${n === 1 ? "" : "s"}`;
}

function SchemeFormModal({
  kennel,
  scheme,
  onClose,
  onSaved,
}: {
  kennel: Kennel;
  scheme: MembershipScheme | null; // null = create
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(scheme?.name ?? "");
  const [price, setPrice] = useState(dollars(scheme?.price));
  const [windowDays, setWindowDays] = useState(
    String(scheme?.windowDays ?? 14)
  );
  const [mode, setMode] = useState<"recurring" | "fixed">(
    scheme?.periods ? "fixed" : "recurring"
  );
  const [months, setMonths] = useState(String(scheme?.recurrence?.months ?? 3));
  const [anchor, setAnchor] = useState(scheme?.recurrence?.anchor ?? "");
  const [periods, setPeriods] = useState<DatePeriod[]>(
    scheme?.periods ?? [{ start: "", end: "" }]
  );
  const [kennelIds, setKennelIds] = useState<string[]>(
    scheme?.kennelIds ?? [kennel.id]
  );
  const [managedKennels, setManagedKennels] = useState<Kennel[]>([kennel]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Schemes can span kennels — offer every managed kennel as a checkbox.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const collected: Kennel[] = [];
      let next: string | undefined = undefined;
      do {
        const res = await api.kennels.list({ cursor: next, limit: 200 });
        if (cancelled) return;
        collected.push(...res.kennels.filter((k) => k.managed));
        next = res.cursor;
      } while (next);
      if (!cancelled && collected.length > 0) setManagedKennels(collected);
    })().catch(() => {
      // Selection falls back to the current kennel only.
    });
    return () => {
      cancelled = true;
    };
  }, [kennel.id]);

  const toggleKennel = (id: string) =>
    setKennelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const save = async () => {
    setBusy(true);
    setErr(null);
    const definition =
      mode === "recurring"
        ? { recurrence: { months: Number(months), anchor }, periods: undefined }
        : {
            recurrence: undefined,
            periods: periods.filter((p) => p.start && p.end),
          };
    try {
      if (scheme) {
        await api.schemes.update(scheme.id, {
          name,
          price: cents(price),
          windowDays: Number(windowDays),
          kennelIds,
          ...definition,
        } satisfies UpdateMembershipSchemeRequest);
      } else {
        await api.schemes.create({
          name,
          price: cents(price),
          windowDays: Number(windowDays),
          kennelIds,
          ...definition,
        });
      }
      onSaved();
    } catch (e) {
      setErr(extractError(e, "Failed to save the scheme"));
      setBusy(false);
    }
  };

  const retire = async () => {
    if (!scheme) return;
    if (!confirm(`Retire "${scheme.name}"? No new periods will open.`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api.schemes.update(scheme.id, { retired: true });
      onSaved();
    } catch (e) {
      setErr(extractError(e, "Failed to retire the scheme"));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={scheme ? `Edit ${scheme.name}` : "New membership scheme"}
      onClose={onClose}
    >
      <div className="mt-3 space-y-3">
        <label className="block">
          <span className={labelCls}>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Quarterly dues"
            aria-label="Scheme name"
            className={inputCls}
          />
        </label>

        <MoneyField
          label="Price per period"
          value={price}
          onChange={setPrice}
        />

        <label className="block">
          <span className={labelCls}>Purchase window (days before start)</span>
          <input
            type="text"
            inputMode="numeric"
            value={windowDays}
            onChange={(e) => setWindowDays(e.target.value)}
            aria-label="Purchase window (days before start)"
            className={inputCls}
          />
        </label>

        <div
          className="flex gap-2"
          role="radiogroup"
          aria-label="Schedule type"
        >
          <button
            type="button"
            role="radio"
            aria-checked={mode === "recurring"}
            onClick={() => setMode("recurring")}
            className={`flex-1 h-10 rounded-lg text-sm font-medium ${
              mode === "recurring"
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            Recurring
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === "fixed"}
            onClick={() => setMode("fixed")}
            className={`flex-1 h-10 rounded-lg text-sm font-medium ${
              mode === "fixed"
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            Fixed periods
          </button>
        </div>

        {mode === "recurring" ? (
          <div className="flex gap-2">
            <label className="block flex-1">
              <span className={labelCls}>Period (months)</span>
              <input
                type="text"
                inputMode="numeric"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                aria-label="Period (months)"
                className={inputCls}
              />
            </label>
            <label className="block flex-1">
              <span className={labelCls}>First period starts</span>
              <input
                type="date"
                value={anchor}
                onChange={(e) => setAnchor(e.target.value)}
                aria-label="First period starts"
                className={inputCls}
              />
            </label>
          </div>
        ) : (
          <div className="space-y-2">
            {periods.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="date"
                  value={p.start}
                  onChange={(e) =>
                    setPeriods((prev) =>
                      prev.map((x, j) =>
                        j === i ? { ...x, start: e.target.value } : x
                      )
                    )
                  }
                  aria-label={`Period ${i + 1} start`}
                  className={inputCls}
                />
                <span className="text-slate-400">–</span>
                <input
                  type="date"
                  value={p.end}
                  onChange={(e) =>
                    setPeriods((prev) =>
                      prev.map((x, j) =>
                        j === i ? { ...x, end: e.target.value } : x
                      )
                    )
                  }
                  aria-label={`Period ${i + 1} end`}
                  className={inputCls}
                />
                <button
                  type="button"
                  aria-label={`Remove period ${i + 1}`}
                  onClick={() =>
                    setPeriods((prev) => prev.filter((_, j) => j !== i))
                  }
                  className="w-10 h-10 shrink-0 rounded-lg text-slate-400 hover:text-red-600"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setPeriods((prev) => [...prev, { start: "", end: "" }])
              }
              className="text-sm font-medium text-slate-700 underline"
            >
              + Add period
            </button>
          </div>
        )}

        {managedKennels.length > 1 && (
          <fieldset>
            <legend className={labelCls}>Covers kennels</legend>
            <div className="space-y-1">
              {managedKennels.map((k) => (
                <label key={k.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={kennelIds.includes(k.id)}
                    onChange={() => toggleKennel(k.id)}
                  />
                  {k.name}
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </div>

      {err && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {err}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
        >
          Cancel
        </button>
        {scheme && !scheme.retired && (
          <button
            type="button"
            onClick={retire}
            disabled={busy}
            className="h-12 flex-1 rounded-lg border border-rose-300 font-medium text-rose-700 disabled:opacity-50"
          >
            Retire
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

// --- Small shared UI --------------------------------------------------------

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-30 bg-slate-900/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function MoneyField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-slate-500">$</span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          aria-label={label}
          className="h-12 flex-1 rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
        />
      </div>
    </label>
  );
}

const labelCls =
  "block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1";
const inputCls =
  "w-full h-12 px-3 rounded-lg border border-slate-300 text-base focus:outline-none focus:ring-2 focus:ring-slate-900";

// Cents <-> dollar-string helpers for the fee inputs.
function money(cents: number | undefined): string {
  return cents === undefined ? "—" : `$${(cents / 100).toFixed(2)}`;
}
function dollars(cents: number | undefined): string {
  return cents === undefined ? "" : (cents / 100).toFixed(2);
}
function cents(dollarStr: string): number {
  const n = Number(dollarStr.trim());
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
}

function extractError(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback;
  const match = err.message.match(/^\d+:\s*(.*)$/);
  const body = match ? match[1] : err.message;
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.error === "string") return parsed.error;
  } catch {
    // fall through
  }
  return body || fallback;
}
